package com.schoolconnect.app.feature.auth

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.feature.rolepicker.RolePickerDialog

@Composable
fun AuthScreen(
    state: AuthUiState,
    onEvent: (AuthUiEvent) -> Unit,
    onBack: () -> Unit
) {
    val scrollState = rememberScrollState()
    Scaffold { paddingValues ->
        ScreenBackground {
            BoxWithConstraints(
                modifier = Modifier.fillMaxSize().padding(paddingValues)
            ) {
                // The OTP card is taller than the available content area on many
                // laptop-sized browser windows. Start the constrained layout early
                // enough that the Continue action remains reachable by scrolling.
                // 1100.dp is taller than most browser viewports, including
                // fullscreen laptop windows. Keep desktop classification based
                // on width and use a lower threshold only for vertical density.
                val shortHeight = maxHeight < 960.dp
                // The browser can be landscape while being just below the old
                // 1120.dp threshold. Keep the orientation check explicit and
                // use a density-safe desktop minimum so a landscape browser
                // viewport does not fall into the portrait-style layout when
                // CSS pixels are converted to Compose dp.
                val landscape = maxWidth > maxHeight
                val wide = landscape && maxWidth >= 500.dp
                val compact = maxWidth < 760.dp
                val horizontalPadding = when {
                    compact -> 20.dp
                    shortHeight -> 32.dp
                    else -> 48.dp
                }
                val verticalPadding = if (shortHeight) 24.dp else 32.dp
                val shortDesktopHeroHeight = (maxHeight - 440.dp).coerceIn(300.dp, 420.dp)
                val scrollable = !wide || shortHeight

                Box(modifier = Modifier.fillMaxSize()) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        if (scrollable) {
                            Column(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxWidth()
                                    .verticalScroll(scrollState),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .widthIn(max = 1800.dp)
                                        .padding(
                                            start = horizontalPadding,
                                            top = verticalPadding,
                                            end = horizontalPadding,
                                            bottom = 32.dp
                                        ),
                                    verticalArrangement = Arrangement.spacedBy(if (shortHeight) 20.dp else 32.dp)
                                ) {
                                    if (wide && shortHeight) {
                                        // Preserve the desktop split in short windows while
                                        // allowing the complete OTP flow to scroll vertically.
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            horizontalArrangement = Arrangement.spacedBy(24.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            LeftMarketingPane(
                                                modifier = Modifier.weight(1f),
                                                compact = false,
                                                landscape = true,
                                                shortHeight = true,
                                                desktopHeroHeight = shortDesktopHeroHeight
                                            )
                                            Column(
                                                modifier = Modifier.weight(0.82f).widthIn(max = 560.dp),
                                                verticalArrangement = Arrangement.spacedBy(16.dp)
                                            ) {
                                                LoginCard(
                                                    modifier = Modifier.fillMaxWidth(),
                                                    state = state,
                                                    compact = false,
                                                    landscape = true,
                                                    // Keep the desktop card proportions on short laptop
                                                    // viewports. The scroll container handles overflow;
                                                    // shrinking the card makes the whole page feel
                                                    // vertically disconnected and compresses the form.
                                                    dense = true,
                                                    onEvent = onEvent
                                                )
                                                RolePickerButton(state, onEvent)
                                            }
                                        }
                                    } else {
                                        LeftMarketingPane(
                                            modifier = Modifier.fillMaxWidth(),
                                            compact = compact,
                                            landscape = landscape,
                                            shortHeight = shortHeight
                                        )
                                        LoginCard(
                                            modifier = Modifier.fillMaxWidth(),
                                            state = state,
                                            compact = compact,
                                            landscape = landscape,
                                            dense = shortHeight,
                                            onEvent = onEvent
                                        )
                                        RolePickerButton(state, onEvent)
                                    }
                                }

                                // In scrollable desktop/laptop layouts the footer belongs to
                                // the content flow. Keeping it outside this column pins it to
                                // the viewport and creates a false gap below the hero.
                                if (!wide) {
                                    FooterLinks(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(
                                                start = horizontalPadding,
                                                top = 0.dp,
                                                end = horizontalPadding,
                                                bottom = 0.dp
                                            ),
                                        compact = compact || shortHeight
                                    )
                                }
                            }
                        } else {
                            Column(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxWidth()
                                    .padding(
                                        start = horizontalPadding,
                                        top = verticalPadding,
                                        end = horizontalPadding,
                                        bottom = 0.dp
                                    ),
                                horizontalAlignment = Alignment.Start
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .fillMaxHeight()
                                        .widthIn(max = 1800.dp),
                                    horizontalArrangement = Arrangement.spacedBy(40.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    LeftMarketingPane(
                                        modifier = Modifier
                                            .weight(1f)
                                            .fillMaxHeight(),
                                        compact = false,
                                        landscape = landscape
                                    )
                                    Column(
                                        // Keep the form column intentionally narrower on tall,
                                        // wide screens so the card stays rectangular and the hero
                                        // gets the extra horizontal room.
                                        modifier = Modifier.widthIn(max = 520.dp),
                                        verticalArrangement = Arrangement.spacedBy(16.dp)
                                    ) {
                                        LoginCard(
                                            modifier = Modifier.fillMaxWidth(),
                                            state = state,
                                            compact = false,
                                            landscape = landscape,
                                            dense = false,
                                            largeDesktop = true,
                                            onEvent = onEvent
                                        )
                                        RolePickerButton(state, onEvent)
                                    }
                                }
                            }
                        }

                        if (!scrollable || wide) {
                            FooterLinks(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(
                                        start = horizontalPadding,
                                        top = 0.dp,
                                        end = horizontalPadding,
                                        bottom = 0.dp
                                    ),
                                compact = compact || shortHeight
                            )
                        }
                    }

                    RolePickerDialog(
                        state = state.rolePicker,
                        onOptionSelected = { onEvent(AuthUiEvent.RolePickerSelectionChanged(it.id)) },
                        onRememberSelectionChanged = { onEvent(AuthUiEvent.RolePickerRememberChanged(it)) },
                        onContinueClicked = { onEvent(AuthUiEvent.RolePickerContinueClicked) },
                        onCloseClicked = { onEvent(AuthUiEvent.RolePickerClosed) }
                    )
                }
            }
        }
    }
}

@Composable
private fun RolePickerButton(state: AuthUiState, onEvent: (AuthUiEvent) -> Unit) {
    if (state.isOtpVerified && !state.rolePicker.isVisible) {
        OutlinedButton(
            onClick = { onEvent(AuthUiEvent.RolePickerRequested) },
            modifier = Modifier.fillMaxWidth()
        ) {
            Text(text = "Choose Role")
        }
    }
}

@Composable
private fun LeftMarketingPane(
    modifier: Modifier = Modifier,
    compact: Boolean,
    landscape: Boolean,
    shortHeight: Boolean = false,
    desktopHeroHeight: androidx.compose.ui.unit.Dp? = null
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(
            when {
                shortHeight -> 16.dp
                compact -> 28.dp
                landscape -> 20.dp
                else -> 28.dp
            }
        )
    ) {
        BrandHeader(
            modifier = Modifier.fillMaxWidth(),
            showLarge = landscape || !compact,
            height = if (shortHeight) 160.dp else 200.dp
        )
        MarketingHeadline(
            text = "A Smarter Way to Connect and Collaborate.",
            modifier = Modifier.padding(top = if (shortHeight || landscape) 10.dp else 18.dp)
        )
        if (shortHeight) {
            HeroIllustration(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(desktopHeroHeight ?: 320.dp)
                    .widthIn(max = 760.dp),
                contentScale = androidx.compose.ui.layout.ContentScale.Fit
            )
        } else {
            HeroIllustration(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(
                        if (desktopHeroHeight != null) {
                            Modifier.height(desktopHeroHeight)
                        } else {
                            Modifier.weight(1f)
                        }
                    ),
                contentScale = androidx.compose.ui.layout.ContentScale.Fit
            )
        }
        TrustFooterBar(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = if (landscape) 980.dp else 880.dp)
        )
    }
}

internal fun formatCountdown(seconds: Int): String {
    val mins = (seconds / 60).toString().padStart(2, '0')
    val secs = (seconds % 60).toString().padStart(2, '0')
    return "$mins:$secs"
}
